import { AxiosError, AxiosResponse } from 'axios';
import instance from 'api';
import { ApiResponse, BaseApi } from 'baseApi';
import { endpoints } from 'endpoints.config';
import {
  IUser,
  IUserAccessPermissions,
  UsersParser,
} from '../pages/manageUsers/models';

const UNKOWN_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.';

export class UsersApi extends BaseApi {
  // get user details
  public static getUserDetails = async (
    url: string,
    id: number
  ): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        url + `?schoolUsersID=${id}`
      );

      if (UsersApi.isResponseSuccessful(result.data)) {
        const data = UsersParser.parseGetUserDetails(result.data.details);
        return UsersApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        }
      }
      return UsersApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  //get user access rights
  public static getUserAccessPermissions = async (id: number): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.manageUsers.updateaccesspermissions + `?schoolUsersID=${id}`
      );

      if (UsersApi.isResponseSuccessful(result.data)) {
        const data = UsersParser.parseGetUserAccessPermissions(
          result.data.details
        );
        return UsersApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        }
      }
      return UsersApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static submitUser = async (
    id: number | null,
    values: IUser
  ): Promise<any> => {
    try {
      let payload = UsersParser.buildUserPayload(values);
      payload = id ? { ...payload, schoolUsersID: id } : payload;

      const url = id
        ? endpoints.manageUsers.updateuser
        : endpoints.manageUsers.addUser;

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        url,
        payload
      );
      if (UsersApi.isResponseSuccessful(result.data)) {
        const data = UsersParser.parseGetUserDetails(result.data.details);
        return UsersApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        }
      }
      return UsersApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static submitAccessPermissions = async (
    id: number,
    values: IUserAccessPermissions
  ): Promise<any> => {
    try {
      let payload = UsersParser.buildAccessPermissionsPayload(values, id);

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.manageUsers.updateaccesspermissions,
        payload
      );

      if (UsersApi.isResponseSuccessful(result.data)) {
        const data = UsersParser.parseGetUserDetails(result.data.details);
        return UsersApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        }
      }
      return UsersApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static deleteUser = async (ids: number[]): Promise<any> => {
    const payload = { idsToDelete: ids };
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.manageUsers.deleteuser,
        payload
      );

      if (UsersApi.isResponseSuccessful(result.data)) {
        const data = UsersParser.parseGetUserDetails(result.data.details);
        return UsersApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        }
      }
      return UsersApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static downloadUserTemplateCSV = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.manageUsers.downloadUserTemplateCSV
      );

      if (result.data) {
        return result;
      } else {
        const errors = [""];
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        }
      }
      return UsersApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };
  
  public static downloadUserListCSV = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.manageUsers.downloadUserListCSV
      );

      if (result.data) {
        return result;
      } else {
        const errors = [""];
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        }
      }
      return UsersApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  }; 

  public static uploadUserListCSV = async (data: FormData): Promise<any> => {
    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    }; 

    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.manageUsers.uploadUserListCSV,
        data,
        config
      );

      if (UsersApi.isResponseSuccessful(result.data)) {
        const data = UsersParser.parseUserBulkDetails(result.data.details);
        const insertionsStatus = data.insertionsStatus;
        let insertionsStatusErr = [];
        
        if(insertionsStatus && insertionsStatus.length){
          for(let i = 0; i < insertionsStatus.length; i++){
            if(insertionsStatus[i].errorMessage){
              insertionsStatusErr.push(insertionsStatus[i].errorMessage);
            }
          }
        }
        
        if(insertionsStatusErr.length){
          return {
            insertionsStatusErr: insertionsStatusErr
          };
        } else {
          return UsersApi.successResponse(data);
        }
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = UsersParser.parseError(errors);
          return UsersApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.errors;
        if (errors) {
          const message = UsersParser.parseCSVError(errors);
          return UsersApi.errorResponse(message);
        }
      }
      return UsersApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };
}
